import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../site/auth";
import { useSeo } from "../../site/ui";
import { fmtDate } from "../../site/pages/Forum";
import { manualApi } from "./LaonaProjects";

/* =========================================================
   레시피 — 회원이 내 프로젝트(수동)를 [레시피 공개하기]로 갤러리에 올린다 (백엔드 app/laona_manual.py recipes_router).
   공개하는 것은 제목 · 소개 · 결선도 · 배선도(모듈을 올린 모습) · 부품 목록. 반투명 · 배선만 배선도와 BOM 은
   [이 레시피로 만들기]로 내 프로젝트에 복사한 뒤 부품을 주문하면 내 프로젝트에서 받는다.
   ========================================================= */

export async function recipeApi(path = "", options = {}) {
  const res = await fetch(`/api/laona/recipes${path}`, {
    credentials: "same-origin",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
    err.status = res.status;
    throw err;
  }
  return data;
}

/* 갤러리 카드 — 배선도(모듈을 올린 모습)를 썸네일로 */
export function RecipeCard({ recipe }) {
  return (
    <Link to={`/laonabot/recipes/${recipe.id}`} className="lb-card">
      <div className="lb-card__thumb lb-card__thumb--recipe">
        <img src={`/api/laona/recipes/${recipe.id}/bread.svg`} alt="" loading="lazy" />
      </div>
      <div className="lb-card__body">
        <div className="lb-card__tags"><span className="lb-tag">레시피</span></div>
        <h3 className="lb-card__title">{recipe.title}</h3>
        {recipe.summary && <p className="lb-card__sum">{recipe.summary}</p>}
        <div className="lb-card__meta">
          <span>{recipe.author}</span>
          <span>{fmtDate(recipe.at)}</span>
          <span>조회 {recipe.views.toLocaleString("ko-KR")}</span>
        </div>
      </div>
    </Link>
  );
}

/* 레시피 공개 · 고치기 팝업 — 내 프로젝트의 [레시피 공개하기] */
export function RecipeDialog({ project, onClose, onSaved }) {
  const ref = useRef(null);
  const [title, setTitle] = useState(project.recipe?.title || project.title);
  const [summary, setSummary] = useState(project.recipe?.summary || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { setError("레시피 제목을 적어 주세요."); return; }
    setBusy(true);
    setError("");
    try {
      await manualApi(`/${project.id}/recipe`, { method: "POST", body: JSON.stringify({ title: title.trim(), summary }) });
      await onSaved();
      ref.current.close();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="lb-dialog lb-dialog--recipe" aria-labelledby="lb-recipe-title" onClose={onClose}
            onClick={(e) => { if (e.target === ref.current && !busy) ref.current.close(); }}>
      <form className="lb-dialog__box" onSubmit={submit}>
        <div className="lb-dialog__head">
          <h2 id="lb-recipe-title">{project.recipe ? "레시피 고치기" : "레시피 공개하기"}</h2>
          <button type="button" className="lb-dialog__x" aria-label="닫기" onClick={() => ref.current.close()}>×</button>
        </div>
        <p className="lb-dialog__lead">
          <b>{project.title}</b>을(를) 레시피 갤러리에 공개합니다. 누구나 결선도 · 배선도(모듈을 올린 모습)와 부품 목록을 볼 수 있고,
          회원은 [이 레시피로 만들기]로 자기 프로젝트에 복사해 쓸 수 있습니다.
        </p>
        <label className="lb-field">
          <span>레시피 제목 <i>*</i></span>
          <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
        </label>
        <label className="lb-field">
          <span>소개 <em>— 무엇을 하는 시제품인지, 만들 때 팁</em></span>
          <textarea rows={5} value={summary} maxLength={2000} onChange={(e) => setSummary(e.target.value)} disabled={busy}
                    placeholder="예: 온습도를 재서 OLED에 보여 주고, 너무 습하면 부저가 울립니다." />
        </label>
        <p className="lb-dialog__note">
          만든 이는 이름 가운데를 가려(홍*동) 표시합니다. 반투명 · 배선만 배선도와 BOM 파일은 공개하지 않습니다.
          프로젝트를 고치고 [완료]를 누르면 레시피에도 그대로 반영되고, 언제든 공개를 내릴 수 있습니다.
        </p>
        {error && <p className="lb-error">{error}</p>}
        <div className="lb-order__acts">
          <button type="button" className="lb-btn" onClick={() => ref.current.close()} disabled={busy}>닫기</button>
          <button type="submit" className="lb-btn lb-btn--primary" disabled={busy}>
            {busy ? "저장 중…" : project.recipe ? "고친 내용 저장" : "레시피 공개"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/* 레시피 상세 — 비회원도 본다 */
export default function LaonaRecipe() {
  const { id } = useParams();
  const { member } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", r: null, error: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: `${state.r ? state.r.title : "레시피"} — LaonaBot`,
    description: state.r?.summary || "회원이 직접 만든 시제품 레시피 — 결선도 · 배선도 · 부품 목록",
    path: `/laonabot/recipes/${id}`,
  });

  useEffect(() => {
    let alive = true;
    setState({ status: "loading", r: null, error: "" });
    recipeApi(`/${id}`)
      .then((r) => alive && setState({ status: "ready", r, error: "" }))
      .catch((err) => alive && setState({ status: "error", r: null, error: err.message }));
    return () => { alive = false; };
  }, [id]);

  async function copy() {
    if (!member) { navigate("/laonabot/signup", { state: { from: `/laonabot/recipes/${id}` } }); return; }
    setBusy(true);
    setError("");
    try {
      const p = await recipeApi(`/${id}/copy`, { method: "POST" });
      navigate(`/laonabot/wiring?manual=${p.id}`);
    } catch (err) {
      setError(err.status === 403 ? "이메일 인증을 마친 회원만 레시피로 만들 수 있습니다." : err.message);
      setBusy(false);
    }
  }

  const r = state.r;
  return (
    <section className="lb-section lb-section--top">
      <div className="lb-wrap lb-recipe">
        <Link to="/laonabot/recipes" className="lb-back">← 레시피 갤러리</Link>
        {state.status === "loading" && <p className="lb-empty">레시피를 불러오는 중입니다…</p>}
        {state.status === "error" && <p className="lb-empty">{state.error || "레시피를 불러오지 못했습니다."}</p>}
        {r && (
          <>
            <header className="lb-head lb-head--row">
              <div>
                <p className="lb-kicker">RECIPE</p>
                <h1>{r.title}</h1>
                <p className="lb-recipe__meta">
                  <span>만든 이 {r.author}</span><span>{fmtDate(r.at)}</span><span>조회 {r.views.toLocaleString("ko-KR")}</span>
                </p>
              </div>
              <div className="lb-head__tools">
                {r.mine ? (
                  <Link to="/laonabot/projects" className="lb-btn">내 프로젝트에서 고치기</Link>
                ) : (
                  <button type="button" className="lb-btn lb-btn--primary" onClick={copy} disabled={busy}>
                    {busy ? "복사하는 중…" : member ? "이 레시피로 만들기" : "가입하고 이 레시피로 만들기"}
                  </button>
                )}
              </div>
            </header>
            {error && <p className="lb-error">{error}</p>}
            {r.summary && <p className="lb-recipe__sum">{r.summary}</p>}

            <h2 className="lb-recipe__h">결선도</h2>
            <div className="lb-recipe__fig"><img src={`/api/laona/recipes/${r.id}/diagram.svg`} alt={`${r.title} 결선도`} /></div>

            <h2 className="lb-recipe__h">브레드보드 배선도</h2>
            <div className="lb-recipe__fig"><img src={`/api/laona/recipes/${r.id}/bread.svg`} alt={`${r.title} 브레드보드 배선도`} /></div>

            <h2 className="lb-recipe__h">부품 목록</h2>
            {r.parts.length ? (
              <ul className="lb-recipe__parts">
                {r.parts.map((p) => (
                  <li key={`${p.kind}:${p.id}`}>
                    {p.image ? <img alt="" src={p.image} /> : <span className="lb-order__noimg" aria-hidden="true" />}
                    <span><b>{p.name}</b>{p.kind === "breadboard" && <small>브레드보드</small>}</span>
                    <span className="lb-recipe__qty">× {p.qty}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="lb-empty">부품 정보가 없습니다.</p>}
            <p className="lb-recipe__note">
              [이 레시피로 만들기]를 누르면 연결도 · 브레드보드 배선이 내 프로젝트로 복사됩니다.
              그곳에서 고쳐 쓰고, [주문하기]로 부품을 주문하면 배선도(반투명 · 배선만)와 전체 BOM도 받을 수 있습니다.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
